import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import { Stack, Typography } from '@mui/material';
import React from 'react';

import { OsTabs } from 'components/atoms/OsTabs';
import { OperatingSystem } from 'utils/osDetection';
import { CliInstructionStep, ItNote } from './cliInstructionTypes';
import { CliCommandBlock } from './CopyableCliBlock';
import InlineItNote from './InlineItNote';
import OsInstallInstructions from './OsInstallInstructions';
import StudyCohortManifestModalLinkStep from './StudyCohortManifestModalLinkStep';
import StudyPendingSlidesLinkStep from './StudyPendingSlidesLinkStep';

interface BuildStepsParams {
  defaultOperatingSystem?: OperatingSystem;
  configureStep?: CliInstructionStep;
  connectStepContent?: React.ReactNode;
  itNotes: Record<'install' | 'connect' | 'upload', ItNote | undefined>;
  onOpenSummary: () => void;
  selectedOs: OperatingSystem;
  setSelectedOs: (operatingSystem: OperatingSystem) => void;
  studyId: string;
  uploadStepContent: React.ReactNode;
  uploadTrackingContent?: React.ReactNode;
  uploadStepTitle?: string;
  validateStep?: CliInstructionStep;
}

const getTerminalOpenGuidance = (selectedOs: OperatingSystem) => {
  if (selectedOs === 'windows') {
    return 'On Windows, press Win + R, type wt or cmd, and press Enter to open Windows Terminal or Command Prompt.';
  }

  if (selectedOs === 'linux') {
    return 'On Linux, press Ctrl + Alt + T to open a terminal window in most desktop environments.';
  }

  return 'On macOS, press Command + Space, type Terminal, and press Enter.';
};

export const ItNoteForUploadStep = ({ itNote, onOpenSummary }: { itNote?: ItNote; onOpenSummary: () => void }) =>
  Boolean(itNote) && (
    <InlineItNote title={itNote!.title} body={itNote!.body} message={itNote!.message} onOpenSummary={onOpenSummary} />
  );

export const buildCliInstructionSteps = ({
  configureStep,
  connectStepContent,
  itNotes,
  onOpenSummary,
  selectedOs,
  defaultOperatingSystem,
  setSelectedOs,
  studyId,
  uploadStepContent,
  uploadTrackingContent,
  uploadStepTitle,
  validateStep,
}: BuildStepsParams): CliInstructionStep[] => {
  const installSteps: CliInstructionStep[] = [
    {
      title: 'Initial setup',
      icon: <ComputerOutlinedIcon color="primary" fontSize="small" />,
      content: (
        <>
          <Typography variant="body2" color="text.secondary">
            One-time AWS CLI setup. Choose your operating system. If you cannot install software, copy the IT note in
            this step and contact your IT team. If you already completed this setup before, continue to the next step.
          </Typography>
          <OsTabs
            selectedOs={selectedOs}
            defaultOperatingSystem={defaultOperatingSystem}
            setSelectedOs={setSelectedOs}
            ariaLabel="AWS CLI install instructions by operating system"
          />
          <OsInstallInstructions selectedOs={selectedOs} />
          <ItNoteForUploadStep itNote={itNotes?.install} onOpenSummary={onOpenSummary} />
        </>
      ),
    },
    {
      title: 'Check AWS CLI',
      icon: <CheckCircleOutlineIcon color="primary" fontSize="small" />,
      content: (
        <>
          <Typography variant="body2" color="text.secondary">
            {getTerminalOpenGuidance(selectedOs)} Run this command to confirm AWS CLI is installed.
          </Typography>
          <CliCommandBlock command="aws --version" />
          <Typography variant="body2" color="text.secondary">
            What you should expect: if the terminal prints a version number such as aws-cli/2.xx, AWS CLI is ready and
            you can continue. If you see a different result, review the initial setup step or contact your IT team.
          </Typography>
        </>
      ),
    },
  ];
  const connectStep: CliInstructionStep = {
    title: 'Connect your AWS CLI',
    icon: <SecurityOutlinedIcon color="primary" fontSize="small" />,
    content: (
      <>
        {connectStepContent}
        <ItNoteForUploadStep itNote={itNotes?.connect} onOpenSummary={onOpenSummary} />
      </>
    ),
  };

  const uploadStep: CliInstructionStep = {
    title: uploadStepTitle || 'Upload slides',
    icon: <TerminalOutlinedIcon color="primary" fontSize="small" />,
    content: (
      <Stack spacing={1}>
        {uploadStepContent}
        <ItNoteForUploadStep itNote={itNotes?.upload} onOpenSummary={onOpenSummary} />
      </Stack>
    ),
  };

  const trackingStep: CliInstructionStep | undefined = uploadTrackingContent
    ? {
        title: 'Track uploaded slides',
        icon: <ManageSearchOutlinedIcon color="primary" fontSize="small" />,
        content: (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              After the upload command finishes, use the options below to confirm that your files were picked up. This
              view does not show a live terminal progress bar.
            </Typography>
            {uploadTrackingContent}
            <StudyCohortManifestModalLinkStep />
            <StudyPendingSlidesLinkStep studyId={studyId} />
          </Stack>
        ),
      }
    : undefined;

  return [
    ...installSteps,
    connectStep,
    ...(configureStep ? [configureStep] : []),
    ...(validateStep ? [validateStep] : []),
    uploadStep,
    ...(trackingStep ? [trackingStep] : []),
  ];
};
